/**
 * Console fetch wrapper. Cookie session only (same-origin /v1 proxy); CSRF token held in memory and sent on
 * every mutation; non-2xx responses become ApiError with the server's error envelope. This client stores nothing
 * in localStorage (the only console use is a TV's wallboard-link key, kept by pages/Wallboard.tsx). Mirrors apps/web/src/api/client.ts conventions (consolidation tracked in ADMIN_PROGRESS.md).
 */
export const CSRF_HEADER = "x-csrf-token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
  /** The `reason` the console API puts on 403s (staff_only, mfa_required, missing_capability). */
  get reason(): string | undefined {
    const d = this.details as { reason?: unknown } | undefined;
    return typeof d?.reason === "string" ? d.reason : undefined;
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export async function api<T>(path: string, opts: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers = new Headers({ accept: "application/json" });
  // A file (CSV import, POD/BOL upload) goes as its raw bytes with its own type; everything else is JSON.
  const raw = typeof Blob !== "undefined" && opts.body instanceof Blob ? opts.body : null;
  if (raw) headers.set("content-type", raw.type || "application/octet-stream");
  else if (opts.body !== undefined) headers.set("content-type", "application/json");
  if (MUTATING.has(method) && csrfToken) headers.set(CSRF_HEADER, csrfToken);
  const res = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: raw ?? (opts.body === undefined ? undefined : JSON.stringify(opts.body)),
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const env = (data as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
    throw new ApiError(res.status, env?.code ?? "error", env?.message ?? `Request failed (${res.status})`, env?.details);
  }
  return data as T;
}

/** The stream couldn't be opened (network, missing route, non-stream answer, 5xx): the caller falls back to the JSON call. */
export class StreamUnavailableError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "StreamUnavailableError";
  }
}

/**
 * POSTs JSON and returns the open `text/event-stream` response (CSRF header and error envelope as api()). A refusal
 * the JSON route would only repeat (400/401/403/409/422...) throws ApiError; anything else that isn't a stream
 * throws StreamUnavailableError so the caller can fall back.
 */
export async function apiEventStream(path: string, body: unknown, opts: { signal?: AbortSignal } = {}): Promise<Response> {
  const headers = new Headers({ accept: "text/event-stream", "content-type": "application/json" });
  if (csrfToken) headers.set(CSRF_HEADER, csrfToken);
  let res: Response;
  try {
    res = await fetch(path, { method: "POST", headers, credentials: "same-origin", body: JSON.stringify(body), signal: opts.signal });
  } catch (e) {
    if (opts.signal?.aborted) throw e;
    throw new StreamUnavailableError(0, "Can't reach the API");
  }
  const type = res.headers.get("content-type") ?? "";
  if (res.ok && type.startsWith("text/event-stream") && res.body) return res;
  let env: { code?: string; message?: string; details?: unknown } | undefined;
  try {
    env = ((await res.json()) as { error?: typeof env }).error;
  } catch {
    env = undefined;
  }
  if (!res.ok && res.status < 500 && res.status !== 404 && res.status !== 405 && env) {
    throw new ApiError(res.status, env.code ?? "error", env.message ?? `Request failed (${res.status})`, env.details);
  }
  throw new StreamUnavailableError(res.status, res.ok ? "The rate stream didn't open" : `Request failed (${res.status})`);
}
